import { motion, useReducedMotion } from 'motion/react';

/**
 * Vetrina prodotto dell'hero: dashboard principale in frame "browser" + tre card
 * reali del prodotto (Multe, Costo Carburante, Stato Flotta) come frame flottanti
 * con angolazioni e posizioni diverse.
 */
const HERO_IMAGE = '/marketing/movofleet-vehicle-hero.png';
const CARD_FINES = '/marketing/movofleet-multe.png';
const CARD_FUEL = '/marketing/movofleet-costo-carburante.png';
const CARD_FLEET = '/marketing/movofleet-conformita.png';

const fadeUp = {
  initial: { opacity: 0, y: 24, filter: 'blur(8px)' },
  animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
};

const cardFrame = 'overflow-hidden rounded-2xl bg-default shadow-overlay ring-1 ring-default';

// Con le animazioni ridotte i frame compaiono già al loro posto.
const useFadeUp = (duration, delay) =>
  useReducedMotion()
    ? {}
    : {
        variants: fadeUp,
        initial: 'initial',
        animate: 'animate',
        transition: { duration, ease: 'easeOut', delay },
      };

function FloatingCard({ src, alt, className, delay }) {
  return (
    <motion.div
      {...useFadeUp(0.7, delay)}
      className={`absolute z-20 hidden ${cardFrame} ${className}`}
    >
      <img
        src={src}
        alt={alt}
        className="w-full"
        loading="lazy"
        decoding="async"
        draggable={false}
      />
    </motion.div>
  );
}

export default function HeroShowcase() {
  return (
    <div className="relative mx-auto mt-12 max-w-6xl px-6 sm:mt-16">
      {/* Frame principale: browser */}
      <motion.div
        {...useFadeUp(0.8, 0.1)}
        className="relative z-10 mx-auto max-w-4xl overflow-hidden rounded-2xl border border-default bg-default shadow-overlay"
      >
        <div className="flex items-center gap-2 border-b border-default bg-muted/60 px-4 py-2.5">
          <span aria-hidden className="size-2.5 rounded-full bg-accent-red-soft/70" />
          <span aria-hidden className="size-2.5 rounded-full bg-accent-amber-soft/70" />
          <span aria-hidden className="size-2.5 rounded-full bg-accent-green-soft/70" />
          <span className="mx-auto rounded-lg bg-default px-3 py-0.5 text-caption text-subtle">
            app.movofleet.com
          </span>
        </div>
        <img
          className="aspect-video w-full object-cover object-top-left"
          src={HERO_IMAGE}
          alt="MovoFleet Operations Room"
          width="2700"
          height="1440"
          loading="eager"
          decoding="async"
          draggable={false}
        />
      </motion.div>

      {/* Card flottanti reali */}
      <FloatingCard
        src={CARD_FINES}
        alt="Riepilogo multe della flotta"
        delay={0.4}
        className="left-2 -top-6 w-36 -rotate-6 md:block lg:left-0 lg:w-44"
      />
      <FloatingCard
        src={CARD_FLEET}
        alt="Stato e disponibilità della flotta"
        delay={0.5}
        className="-bottom-6 left-2 w-36 rotate-3 md:block lg:left-0 lg:w-44"
      />
      <FloatingCard
        src={CARD_FUEL}
        alt="Costo carburante mensile"
        delay={0.6}
        className="-bottom-6 right-2 w-36 -rotate-3 md:block lg:right-0 lg:w-44"
      />

      {/* Sfumatura inferiore verso il background */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -bottom-1 z-30 h-24 bg-linear-to-b from-transparent to-default"
      />
    </div>
  );
}
